import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Nav from './components/ghana/Nav';
import Footer from './components/ghana/footer';
import { ReferralProvider } from './components/ghana/ReferralContext';
import AgeVerification from './components/AgeVerification';

// ──────────────────────────────────────────────────────────────
// Lazy-loaded pages (code-splitting)  implemented by code splitting to improve the performance of the app
// ──────────────────────────────────────────────────────────────
const GhPageHub            = lazy(() => import('./pages/GhPageHub'));
const Howtoplay            = lazy(() => import('./pages/Howtoplay'));
const Prizes               = lazy(() => import('./pages/prizes'));
const Winners              = lazy(() => import('./pages/winner'));
const FAQSection           = lazy(() => import('./pages/faq'));
const TermsAndConditions   = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy        = lazy(() => import('./pages/privacypolicy'));
const IcholaMegaboxLanding = lazy(() => import('./pages/stationhub'));
const ExternalRedirect     = lazy(() => import('./pages/ExternalRedirect'));

// ──────────────────────────────────────────────────────────────
// Fallback while lazy components load
// ──────────────────────────────────────────────────────────────
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <div>Loading...</div>
  </div>
);

// ──────────────────────────────────────────────────────────────
// Constants for IP caching
// ──────────────────────────────────────────────────────────────
const defaultIpInfo = {
  ip: '0.0.0.0',
  city: 'Unknown',
  region: 'Unknown',
  country: 'Unknown',
  loc: '0,0',
  org: 'Unknown',
  postal: 'Unknown',
  timezone: 'UTC'
};

const ONE_DAY         = 24 * 60 * 60 * 1000;
const IP_CACHE_KEY    = 'ipInfoCache';
const IP_TIMESTAMP_KEY = 'ipInfoTimestamp';

// ──────────────────────────────────────────────────────────────
// Main App component
// ──────────────────────────────────────────────────────────────
function App() {
  const location       = useLocation();
  const hideNavFooter  = location.pathname === '/stationhub';

  // ---------- IP state ----------
  const [ipInfo, setIpInfo] = useState(() => {
    try {
      const cachedData      = localStorage.getItem(IP_CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(IP_TIMESTAMP_KEY);
      if (
        cachedData &&
        cachedTimestamp &&
        Date.now() - Number(cachedTimestamp) < ONE_DAY
      ) {
        return JSON.parse(cachedData);
      }
    } catch (err) {
      /* ignore localStorage errors */
    }
    return defaultIpInfo;                // always return a valid object
  });

  const [ipError,   setIpError]   = useState(null);
  const [ipFetched, setIpFetched] = useState(false);

  // ---------- Fetch IP once on mount ----------
  useEffect(() => {
    let isMounted  = true;
    const controller = new AbortController();

    const fetchIpInfo = async () => {
      // Skip if we already have valid cached data
      if (ipInfo.ip !== defaultIpInfo.ip && !ipFetched) {
        setIpFetched(true);
        return;
      }

      try {
        const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
          signal: controller.signal,
          cache: 'no-cache'
        });
        if (!response.ok) throw new Error(`Network response error: ${response.status}`);

        const data = await response.json();
        if (!isMounted) return;

        const transformed = {
          ip: data.ip                     || defaultIpInfo.ip,
          city: data.city                 || defaultIpInfo.city,
          region: data.region             || defaultIpInfo.region,
          country: data.country           || defaultIpInfo.country,
          loc: (data.latitude && data.longitude)
                 ? `${data.latitude},${data.longitude}`
                 : defaultIpInfo.loc,
          org: data.organization          || defaultIpInfo.org,
          postal: data.postal             || defaultIpInfo.postal,
          timezone: data.timezone         || defaultIpInfo.timezone
        };

        try {
          localStorage.setItem(IP_CACHE_KEY,    JSON.stringify(transformed));
          localStorage.setItem(IP_TIMESTAMP_KEY, Date.now().toString());
        } catch (e) {
          /* ignore caching errors */
        }

        setIpInfo(transformed);
        setIpError(null);
        setIpFetched(true);

      } catch (err) {
        if (err.name === 'AbortError') return;
        if (isMounted) {
          setIpError(err.message);
          setIpFetched(true);
        }
      }
    };

    // Use requestIdleCallback if available
    if (window.requestIdleCallback) {
      window.requestIdleCallback(fetchIpInfo);
    } else {
      setTimeout(fetchIpInfo, 100);
    }

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [ipInfo.ip, ipFetched]);

  // ---------- Dev logging ----------
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('IP info:', ipInfo);
      if (ipError) console.log('IP error:', ipError);
    }
  }, [ipInfo, ipError]);

  // ---------- JSXe ----------
  return (
    <div>
      <ReferralProvider>
        {!hideNavFooter && <Nav />}

        {ipError && process.env.NODE_ENV === 'development' && (
          <div style={{ background: '#ffe6e6', color: '#cc0000', padding: 10, textAlign: 'center' }}>
            IP location service error: {ipError}
          </div>
        )}

        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/"                 element={<GhPageHub ipInfo={ipInfo} />} index />
            <Route path="/gh"               element={<GhPageHub ipInfo={ipInfo} />} />
            <Route path="/howtoplay"        element={<Howtoplay />} />
            <Route path="/prizes"           element={<Prizes />} />
            <Route path="/winners"          element={<Winners />} />
            <Route path="/faq"              element={<FAQSection />} />
            <Route path="/termsConditions"  element={<TermsAndConditions />} />
            <Route path="/privacypolicy"    element={<PrivacyPolicy />} />

            {/* This page hides Nav + Footer */}
            <Route path="/stationhub"       element={<IcholaMegaboxLanding />} />

            <Route path="/dashboard"        element={<ExternalRedirect url="http://xrafflegames.com" />} />
            <Route path="*"                 element={<Navigate replace to="/" />} />
          </Routes>
        </Suspense>

        {!hideNavFooter && <Footer />}
      </ReferralProvider>
    </div>
  );
}

export default App;


